import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AssessmentOutlined, RefreshOutlined, SearchOutlined } from "@mui/icons-material";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { reportsService } from "../../services";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toMoney = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return `Q${parsed.toFixed(2)}`;
};

function ProductUtilityReportPage() {
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await reportsService.getProductsProfitReport({
        q: String(query || "").trim() || undefined,
      });
      setRows(toArray(data?.results || data));
    } catch {
      setRows([]);
      showSnack("No se pudo cargar el reporte por producto.", "error");
    } finally {
      setLoading(false);
    }
  }, [query, showSnack]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const summary = useMemo(() => {
    const pricedRows = rows.filter((row) => row?.sale_price !== null);
    return {
      totalRows: rows.length,
      pricedRows: pricedRows.length,
      withoutPrice: rows.length - pricedRows.length,
    };
  }, [rows]);

  const columns = useMemo(
    () => [
      {
        field: "product",
        headerName: "Producto",
        minWidth: 240,
        flex: 1.2,
        renderCell: ({ row }) => (
          <Box sx={{ minWidth: 0, py: 0.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
              {row?.product || "-"}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {row?.product_code || "Sin codigo"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "format",
        headerName: "Formato",
        minWidth: 160,
        flex: 0.8,
      },
      {
        field: "qty_label",
        headerName: "Cantidad / equivalencia",
        minWidth: 220,
        flex: 1,
      },
      {
        field: "cost_price",
        headerName: "Costo",
        minWidth: 120,
        flex: 0.6,
        valueGetter: (_, row) => toMoney(row?.cost_price),
      },
      {
        field: "sale_price",
        headerName: "Precio venta",
        minWidth: 130,
        flex: 0.7,
        valueGetter: (_, row) => toMoney(row?.sale_price),
      },
      {
        field: "utility_amount",
        headerName: "Utilidad",
        minWidth: 160,
        flex: 0.8,
        renderCell: ({ row }) => {
          const utility = row?.utility_amount !== null ? toMoney(row?.utility_amount) : "-";
          const utilityPct = row?.utility_pct ? `${Number(row.utility_pct).toFixed(2)}%` : "";
          return (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {utility}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {utilityPct || "Sin precio activo"}
              </Typography>
            </Box>
          );
        },
      },
    ],
    []
  );

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2}>
        <MainText
          title="Reporte por producto y su utilidad"
          subtitle="Costo, precio vigente y utilidad por presentacion"
          icon={AssessmentOutlined}
        />
        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField
            size="small"
            label="Buscar"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={loadRows}>
            Refrescar
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <Typography variant="body2">Filas: {summary.totalRows}</Typography>
          <Typography variant="body2">Con precio vigente: {summary.pricedRows}</Typography>
          <Typography variant="body2">Sin precio vigente: {summary.withoutPrice}</Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ height: "calc(100vh - 280px)", minHeight: 420 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          pageSizeOptions={[20, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 20, page: 0 } },
            sorting: { sortModel: [{ field: "product", sort: "asc" }] },
          }}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#757B2B",
              color: "#ffffff",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover": {
              backgroundColor: "#757B2B !important",
            },
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator": {
              color: "#ffffff !important",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(16, 23, 60, 0.08)",
            },
          }}
        />
      </Paper>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}

export default ProductUtilityReportPage;
